import React, { useState } from 'react';
import { TermsType } from '../../services/aiTermsService';
import { useDatePreferences } from '../../hooks/useDatePreferences';

interface AITermsModalProps {
    isOpen: boolean;
    onAccept: () => void;
    onClose?: () => void;
    isSubmitting?: boolean;
    currentVersion?: string;
    userVersion?: string | null;
    needsUpdate?: boolean;
    termsType?: TermsType;
}

// Aptive Index terms content (business/HR focused)
const AptiveIndexTermsContent: React.FC = () => (
    <>
        <p>
            This AI Use Policy ("Policy") governs how administrators, users, and client organizations ("Users") may use Aptive Index's platform and its artificial intelligence ("AI") features. By accessing or using any AI-powered functionality within Aptive Index, you agree to the terms of this Policy.
        </p>

        <h3>1. Purpose of AI Features</h3>
        <p>
            Aptive Index incorporates AI-powered tools designed to provide supplemental insights and recommendations in support of human resources, leadership development, and organizational decision-making. These tools are informational only and are not intended to replace qualified professional judgment.
        </p>

        <h3>2. Human Oversight Required</h3>
        <ul>
            <li>All hiring, promotion, management, or termination decisions must be made by qualified humans, not by AI.</li>
            <li>AI outputs are not scoring assessments and should not be relied upon as the sole or final factor in any decision.</li>
            <li>Users must apply independent judgment and ensure compliance with all applicable laws when interpreting AI insights.</li>
        </ul>

        <h3>3. Prohibited Uses</h3>
        <p>Users must not:</p>
        <ul>
            <li><strong>Rely on AI outputs as a substitute for:</strong>
                <ul>
                    <li>Employment law or HR compliance guidance</li>
                    <li>Professional coaching or mediation</li>
                    <li>Medical, psychological, or legal advice</li>
                </ul>
            </li>
            <li>Use AI insights to justify or avoid difficult conversations in the workplace that should be addressed by human leadership.</li>
            <li>Use the Services in any way that could cause harm to oneself or others.</li>
        </ul>

        <h3>4. Mental Health & Safety Disclaimer</h3>
        <ul>
            <li>Aptive Index is not designed for crisis support or mental health emergencies.</li>
            <li>If you are experiencing distress, suicidal thoughts, or other urgent mental health concerns, you must immediately seek help from a qualified professional or contact emergency services (for example, dial 988 in the U.S. or your local crisis hotline).</li>
            <li>AI-generated outputs must never be relied upon to manage or respond to personal safety, health, or mental health situations.</li>
        </ul>

        <h3>5. Liability & Responsibility</h3>
        <ul>
            <li>Aptive Index disclaims liability for any harm caused by misuse or over-reliance on AI-generated recommendations.</li>
            <li>The Client organization and its Users are solely responsible for ensuring that all AI outputs are applied ethically, lawfully, and with appropriate human oversight.</li>
        </ul>

        <h3>6. Acknowledgment</h3>
        <p>By clicking "I Agree" or otherwise using the AI features, you confirm that:</p>
        <ul>
            <li>You have read, understood, and agree to abide by this Policy.</li>
            <li>You accept responsibility for ensuring ethical use of AI features.</li>
            <li>You acknowledge that AI insights are supplemental and not determinative.</li>
        </ul>
    </>
);

// Aria Chat terms content (consumer-friendly)
const AriaChatTermsContent: React.FC = () => (
    <>
        <p>
            Welcome to Aria! This AI Use Policy ("Policy") explains how you can use Aria's AI-powered features. By using Aria, you agree to this Policy.
        </p>

        <h3>1. What Aria Does</h3>
        <p>
            Aria is your AI companion for personal growth and self-discovery. Aria offers insights and suggestions based on your personality assessment to help you understand yourself and your relationships better. These insights are meant to be helpful conversation starters—not definitive answers.
        </p>

        <h3>2. Aria Is Not a Professional</h3>
        <p>Aria is an AI assistant, not a licensed professional. Please keep in mind:</p>
        <ul>
            <li>Aria's suggestions are for informational and entertainment purposes only.</li>
            <li>Aria cannot replace advice from doctors, therapists, counselors, lawyers, or other qualified professionals.</li>
            <li>Always use your own judgment when considering Aria's insights.</li>
        </ul>

        <h3>3. Important Safety Information</h3>
        <ul>
            <li>Aria is not designed for crisis support or emergencies.</li>
            <li><strong>If you're in crisis:</strong> Please reach out to a mental health professional or call a crisis line immediately (988 in the U.S., or your local emergency services).</li>
            <li>Never rely on Aria for urgent health, safety, or emergency decisions.</li>
        </ul>

        <h3>4. Your Responsibility</h3>
        <ul>
            <li>Use Aria thoughtfully and for its intended purpose of personal growth.</li>
            <li>Don't use Aria in ways that could harm yourself or others.</li>
            <li>Remember that AI insights are suggestions to consider, not instructions to follow.</li>
        </ul>

        <h3>5. Our Commitment</h3>
        <p>
            We're committed to making Aria helpful and safe. While we work hard to provide valuable insights, we can't guarantee that Aria's suggestions will always be perfect for your situation.
        </p>

        <h3>6. Agreement</h3>
        <p>By clicking "I Agree," you confirm that:</p>
        <ul>
            <li>You understand Aria is an AI assistant, not a professional.</li>
            <li>You'll use Aria responsibly for personal growth and insight.</li>
            <li>You know to seek professional help for serious concerns.</li>
        </ul>
    </>
);

const AITermsModal: React.FC<AITermsModalProps> = ({
    isOpen,
    onAccept,
    isSubmitting = false,
    currentVersion = '1.0.0',
    userVersion,
    needsUpdate = false,
    termsType = 'aptive_index'
}) => {
    const { formatMedium } = useDatePreferences();
    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
    const [hasReadTerms, setHasReadTerms] = useState(false);

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollTop + clientHeight >= scrollHeight - 10) {
            setHasScrolledToBottom(true);
        }
    };

    const handleAccept = () => {
        if (hasReadTerms && hasScrolledToBottom) {
            onAccept();
        }
    };

    if (!isOpen) return null;

    const isAriaChatTerms = termsType === 'aria_chat';
    const title = isAriaChatTerms ? 'Aria – AI Use Policy' : 'Aptive Index – AI Use Policy';
    
    const overlayClassName = 'ai-terms-modal-overlay';

    return (
        <div className={overlayClassName}>
            <div className="ai-terms-modal-content">
                <div className="ai-terms-modal-header">
                    <h2>{title}</h2>
                </div>
                
                <div className="ai-terms-modal-body">
                    <div className="ai-terms-effective-date">
                        <strong>Effective Date:</strong> {formatMedium(new Date())}
                        {needsUpdate && userVersion && (
                            <div className="ai-terms-update-notice">
                                <strong>⚠️ Terms Updated:</strong> You previously accepted version {userVersion}. 
                                Please review and accept the updated terms for v{currentVersion}.
                            </div>
                        )}
                    </div>
                    
                    <div className="ai-terms-content" onScroll={handleScroll}>
                        {!hasScrolledToBottom && (
                            <div className="ai-terms-scroll-indicator">
                                <div className="scroll-hint">
                                    <span className="scroll-icon">↓</span>
                                    <span className="scroll-text">Please scroll down to read the full terms</span>
                                </div>
                            </div>
                        )}
                        
                        {isAriaChatTerms ? <AriaChatTermsContent /> : <AptiveIndexTermsContent />}
                    </div>
                </div>

                <div className="ai-terms-modal-footer">
                    <div className="ai-terms-checkbox">
                        <input
                            type="checkbox"
                            id="read-terms"
                            checked={hasReadTerms}
                            onChange={(e) => setHasReadTerms(e.target.checked)}
                        />
                        <label htmlFor="read-terms">
                            I have read and understood the AI Use Policy
                        </label>
                    </div>
                    
                    <button
                        className="ai-terms-accept-button"
                        onClick={handleAccept}
                        disabled={!hasReadTerms || !hasScrolledToBottom || isSubmitting}
                    >
                        {isSubmitting ? 'Processing...' : 'I Agree'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AITermsModal;
